import React, {FC} from "react"

import {Body, H3, H4, Label} from "@irati/ui/src/Typography"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {UserRedemptionReportSavings} from "@irati/types"
import authentication from "@irati/auth"

import BreakdownDetails from "../BreakdownDetails"

interface Props {
  savings: UserRedemptionReportSavings
  showBreakdownDetails?: boolean
}

const Breakdown: FC<Props> = ({savings, showBreakdownDetails}) => {
  const hasFixedSavings = savings.fixed?.value > 0
  const hasEstimatedSavings = savings.estimated?.value > 0
  const hasFP = authentication.apps("wallet.treatTokens")
  const hasRP = authentication.apps("wallet.flexiRewards")
  const fpCopy = hasFP ? <FormattedMessage id="platform.mySavings.breakdown.fixed.tooltip.bodyFp" /> : ""
  const rpCopy = hasRP ? <FormattedMessage id="platform.mySavings.breakdown.fixed.tooltip.bodyRp" /> : ""

  const Comp = showBreakdownDetails ? H3 : H4

  return (
    <Flex alignItems={showBreakdownDetails ? "flex-start" : "center"} wrap="wrap">
      {hasFixedSavings && (
        <Flex direction="column">
          <Flex alignItems="center" marginRight={2}>
            <Comp as="span" display="inline">
              <FormattedCurrency value={savings.fixed?.value} currency={savings.fixed?.currency} />
            </Comp>
            <Label small as="span" display="inline" marginLeft marginRight uppercase>
              <FormattedMessage id="platform.mySavings.breakdown.fixed.label" />
            </Label>
            <Tooltip
              placement={OverlayPlacement.Bottom}
              tooltipChildren={
                <>
                  <H4>
                    <FormattedMessage id="platform.mySavings.breakdown.fixed.tooltip.header" />
                  </H4>
                  <Body small>
                    <FormattedMessage id="platform.mySavings.breakdown.fixed.tooltip.body" values={{fpCopy, rpCopy}} />
                  </Body>
                  {!showBreakdownDetails && <BreakdownDetails breakdown={savings.fixed?.breakdown} />}
                </>
              }>
              <Icon icon={IconTypes.circleQuestion} extraLarge />
            </Tooltip>
          </Flex>
          {showBreakdownDetails && (
            <Spacing margin={[0, 2, 2]}>
              <BreakdownDetails breakdown={savings.fixed?.breakdown} />
            </Spacing>
          )}
        </Flex>
      )}
      {hasEstimatedSavings && (
        <Flex direction="column">
          <Flex alignItems="center">
            <Comp as="span" display="inline">
              <FormattedCurrency value={savings.estimated?.value} currency={savings.estimated?.currency} />
            </Comp>
            <Label small as="span" display="inline" marginLeft marginRight uppercase>
              <FormattedMessage id="platform.mySavings.breakdown.estimated.label" />
            </Label>
            <Tooltip
              placement={OverlayPlacement.Bottom}
              tooltipChildren={
                <>
                  <H4>
                    <FormattedMessage id="platform.mySavings.breakdown.estimated.tooltip.header" />
                  </H4>
                  <Body small>
                    <FormattedMessage id="platform.mySavings.breakdown.estimated.tooltip.body" />
                  </Body>
                  {!showBreakdownDetails && <BreakdownDetails breakdown={savings.estimated?.breakdown} />}
                </>
              }>
              <Icon icon={IconTypes.circleQuestion} extraLarge />
            </Tooltip>
          </Flex>
          {showBreakdownDetails && (
            <Spacing margin={[0, 2, 2]}>
              <BreakdownDetails breakdown={savings.estimated?.breakdown} />
            </Spacing>
          )}
        </Flex>
      )}
    </Flex>
  )
}

export default Breakdown
